/**
 * Human-readable recurrence labels for the site scheduling calendar.
 *
 * The calendar backend expands recurrence server-side and hands the frontend a
 * parsed descriptor ({freq, interval, byday, bymonthday, ...}) rather than an
 * RFC-5545 string. This module turns that descriptor into the short phrase a
 * scheduler shows next to an event ("Every weekday", "Every 2 weeks on Monday,
 * Tuesday", "Every month on day 15"), localized through the caller's i18n `t`.
 *
 * Its wording is locked byte-for-byte by a characterization test, so no
 * RRULE text is ever parsed on the client.
 *
 * Contains:
 * - recurrenceLabel — descriptor + t -> localized label (named export)
 *
 * Returns plain JS — no JSX, no redux.
 */

const REVERSE_DAY_MAP = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

const DAY_NAMES = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR'];

/** Ordinal position word, matching the legacy calendar wording. */
function getOrdinalText(num) {
  if (num === -1) return 'last';
  if (num === 1) return 'first';
  if (num === 2) return 'second';
  if (num === 3) return 'third';
  if (num === 4) return 'fourth';
  return `${num}th`;
}

/**
 * Normalize the backend descriptor into the flat shape the label switch reads.
 * `byday` entries may carry an ordinal prefix (e.g. "1MO" = first Monday); an
 * ordinal entry becomes weekday/weekdayOrdinal and is excluded from weekDays.
 */
function toParams(descriptor) {
  const freq = descriptor.freq ? String(descriptor.freq).toLowerCase() : 'never';
  const interval = descriptor.interval ? parseInt(descriptor.interval, 10) : 1;
  const params = { repeatType: freq, interval, weekDays: [] };

  const byday = Array.isArray(descriptor.byday) ? descriptor.byday : [];
  byday.forEach(entry => {
    const match = String(entry).match(/^(-?\d+)?([A-Z]{2})$/);
    if (!match) return;
    const [, ordinal, dayCode] = match;
    if (ordinal) {
      params.weekdayOrdinal = parseInt(ordinal, 10);
      params.weekday = REVERSE_DAY_MAP[dayCode];
      return;
    }
    params.weekDays.push(dayCode);
  });

  if (descriptor.bymonthday != null) {
    params.monthDay = parseInt(descriptor.bymonthday, 10);
  }

  return params;
}

/**
 * @param {?object} descriptor - Parsed recurrence descriptor from the calendar
 *   API (`recurrence.parsed`): {freq, interval, byday, bymonthday, until, count}.
 *   `null`/`undefined` means the event does not repeat.
 * @param {?Function} t - i18next translate fn (key, fallback, interpolation).
 *   When omitted, the English fallback wording is returned directly.
 * @returns {string} The localized recurrence label.
 */
export function recurrenceLabel(descriptor, t = null) {
  if (!descriptor || !descriptor.freq) {
    return t ? t('calendar.rrule.doesNotRepeat', 'Does not repeat') : 'Does not repeat';
  }

  const { repeatType, interval, weekDays, monthDay, weekday, weekdayOrdinal } = toParams(descriptor);

  const dayNamesTranslated = t
    ? {
        SU: t('calendar.weekdays.sunday', 'Sunday'),
        MO: t('calendar.weekdays.monday', 'Monday'),
        TU: t('calendar.weekdays.tuesday', 'Tuesday'),
        WE: t('calendar.weekdays.wednesday', 'Wednesday'),
        TH: t('calendar.weekdays.thursday', 'Thursday'),
        FR: t('calendar.weekdays.friday', 'Friday'),
        SA: t('calendar.weekdays.saturday', 'Saturday'),
      }
    : DAY_NAMES;

  const intervalArg = interval > 1 ? ` ${interval}` : '';
  const pluralArg = interval > 1 ? 's' : '';

  switch (repeatType) {
    case 'daily':
      if (weekDays.length === 5 && weekDays.every(day => WEEKDAY_CODES.includes(day))) {
        return t ? t('calendar.rrule.everyWeekday', 'Every weekday') : 'Every weekday';
      }
      if (weekDays.length > 0) {
        const dayNames = weekDays.map(day => dayNamesTranslated[day]).join(', ');
        return t
          ? t('calendar.rrule.weekOn', 'Every {{interval}} week{{plural}} on {{days}}', {
              interval: intervalArg,
              plural: pluralArg,
              days: dayNames,
            })
          : interval > 1
            ? `Every ${interval} weeks on ${dayNames}`
            : `Every week on ${dayNames}`;
      }
      return t
        ? t('calendar.rrule.daily', 'Every {{interval}} day{{plural}}', {
            interval: intervalArg,
            plural: pluralArg,
          })
        : interval > 1
          ? `Every ${interval} days`
          : 'Every day';

    case 'weekly':
      if (weekDays.length > 0) {
        const dayNames = weekDays.map(day => dayNamesTranslated[day]).join(', ');
        return t
          ? t('calendar.rrule.weeklyOn', 'Every {{interval}} week{{plural}} on {{days}}', {
              interval: intervalArg,
              plural: pluralArg,
              days: dayNames,
            })
          : interval > 1
            ? `Every ${interval} weeks on ${dayNames}`
            : `Every week on ${dayNames}`;
      }
      return t
        ? t('calendar.rrule.weekly', 'Every {{interval}} week{{plural}}', {
            interval: intervalArg,
            plural: pluralArg,
          })
        : interval > 1
          ? `Every ${interval} weeks`
          : 'Every week';

    case 'monthly':
      if (monthDay) {
        return t
          ? t('calendar.rrule.monthlyOnDay', 'Every {{interval}} month{{plural}} on day {{day}}', {
              interval: intervalArg,
              plural: pluralArg,
              day: monthDay,
            })
          : interval > 1
            ? `Every ${interval} months on day ${monthDay}`
            : `Every month on day ${monthDay}`;
      }
      if (weekday !== undefined && weekdayOrdinal) {
        const dayName = Object.keys(REVERSE_DAY_MAP)[weekday];
        const ordinalText = t
          ? t(`calendar.rrule.ordinals.${getOrdinalText(weekdayOrdinal)}`, getOrdinalText(weekdayOrdinal))
          : getOrdinalText(weekdayOrdinal);
        return t
          ? t('calendar.rrule.monthlyOnWeekday', 'Every {{interval}} month{{plural}} on the {{ordinal}} {{day}}', {
              interval: intervalArg,
              plural: pluralArg,
              ordinal: ordinalText,
              day: dayName,
            })
          : interval > 1
            ? `Every ${interval} months on the ${ordinalText} ${dayName}`
            : `Every month on the ${ordinalText} ${dayName}`;
      }
      return t
        ? t('calendar.rrule.monthly', 'Every {{interval}} month{{plural}}', {
            interval: intervalArg,
            plural: pluralArg,
          })
        : interval > 1
          ? `Every ${interval} months`
          : 'Every month';

    default:
      return descriptor.rrule_string ?? '';
  }
}
